import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

/**
 * PanelCoordinator
 *
 * Coordinates the left (ToC) and right (chat/tools) drawer panels to ensure
 * the center content area maintains a minimum width of 450px.
 *
 * Responsibilities:
 * - On page load: Determine which panels should be open based on localStorage and window size
 * - On window resize: Close panels as needed to maintain center width
 * - On panel open/close: Prevent violations of the 450px rule
 * - During active resize: Dynamically open/close opposite panel while maintaining constraints
 *
 * Mobile behavior (< 767px): Only one panel open at a time, full-width overlay
 */
export const PanelCoordinator = ({ coursenum }) => {
  const MIN_CENTER_WIDTH = 450
  const MOBILE_BREAKPOINT = 767
  const ICONBAR_WIDTH = 46
  // const RIGHT_PANEL_MAX_WIDTH = null // Set to a number (e.g., 800) to add constraint, null for no limit

  // Track state during active resizing
  const resizeState = useRef({
    isResizing: false,
    resizingPanel: null, // 'left' or 'right'
    oppositeWasOpen: false,
  })

  /**
   * Calculate the current center content width
   */
  const getCenterWidth = () => {
    const leftDrawer = document.getElementById('left-drawer')
    const rightDrawer = document.getElementById('chatbot_drawer')

    const leftWidth = leftDrawer ? leftDrawer.offsetWidth : 0
    const rightWidth = rightDrawer ? rightDrawer.offsetWidth : 0

    // Iconbar is always present in reader mode
    const iconbarWidth = ICONBAR_WIDTH

    return window.innerWidth - leftWidth - rightWidth - iconbarWidth
  }

  /**
   * Check if a panel is currently open
   */
  const isPanelOpen = panel => {
    if (panel === 'left') {
      return document.body.classList.contains('has-left-drawer')
    } else if (panel === 'right') {
      return document.body.classList.contains('has-chat-drawer')
    }
    return false
  }

  /**
   * Get panel widths
   */
  const getPanelWidth = panel => {
    if (panel === 'left') {
      const drawer = document.getElementById('left-drawer')
      return drawer ? drawer.offsetWidth : 0
    } else if (panel === 'right') {
      const drawer = document.getElementById('chatbot_drawer')
      return drawer ? drawer.offsetWidth : 0
    }
    return 0
  }

  /**
   * Close a panel
   */
  const closePanel = panel => {
    if (panel === 'left') {
      window.dispatchEvent(
        new CustomEvent('close-left-drawer', {
          detail: { restoreFocus: false, fromCoordinator: true },
        })
      )
    } else if (panel === 'right') {
      window.dispatchEvent(
        new CustomEvent('close-chat-drawer', {
          detail: { fromCoordinator: true },
        })
      )
    }
  }

  /**
   * Open a panel
   */
  const openPanel = panel => {
    if (panel === 'left') {
      window.dispatchEvent(
        new CustomEvent('open-left-drawer', {
          detail: { fromCoordinator: true },
        })
      )
    } else if (panel === 'right') {
      // Right panel uses class and localStorage directly
      if (!document.body.classList.contains('has-chat-drawer')) {
        document.body.classList.add('has-chat-drawer')

        const readerLayout = document.getElementById('reader_layout')
        const iconbar = document.getElementById('reader_iconbar')
        const drawer = document.getElementById('chatbot_drawer')

        if (readerLayout && iconbar && drawer) {
          const iconbarWidth = iconbar.offsetWidth
          const drawerWidth = drawer.offsetWidth
          const arrow = document.querySelector('.navigation_arrow_right')

          readerLayout.style.marginRight = `${drawerWidth + iconbarWidth}px`
          if (arrow) arrow.style.right = `${drawerWidth + iconbarWidth + 30}px`
        }

        try {
          localStorage.setItem(`chatDisplayWin-${coursenum}`, 'true')
        } catch (e) {
          // ignore
        }

        // Restore the active drawer state from localStorage
        try {
          const activeDrawer = localStorage.getItem(
            `chatActiveDrawer-${coursenum}`
          )
          if (activeDrawer) {
            document.dispatchEvent(
              new CustomEvent('drawer-state-change', {
                detail: { activeDrawer },
              })
            )
          }
        } catch (e) {
          // ignore
        }
      }
    }
  }

  /**
   * Apply coordination rules on page load
   */
  const coordinateOnLoad = () => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT

    // Get localStorage states
    const leftShouldBeOpen =
      localStorage.getItem(`leftDrawerReaderOpen-${coursenum}`) !== 'false'
    const rightShouldBeOpen =
      localStorage.getItem(`chatDisplayWin-${coursenum}`) === 'true'

    if (isMobile) {
      // Mobile: only one panel at a time, prioritize right
      if (rightShouldBeOpen && leftShouldBeOpen) {
        closePanel('left')
      }
      return
    }

    // Desktop: Check if both can be open
    if (leftShouldBeOpen && rightShouldBeOpen) {
      const leftWidth = getPanelWidth('left')
      const rightWidth = getPanelWidth('right')
      const centerWidth =
        window.innerWidth - leftWidth - rightWidth - ICONBAR_WIDTH

      if (centerWidth >= MIN_CENTER_WIDTH) {
        // Both can be open
        return
      }

      // Both can't be open, try right only
      closePanel('left')

      // Check if right alone meets requirement
      const centerWithRightOnly = window.innerWidth - rightWidth - ICONBAR_WIDTH
      if (centerWithRightOnly < MIN_CENTER_WIDTH) {
        // Right alone doesn't work either, close it too
        closePanel('right')
      }
    } else if (leftShouldBeOpen) {
      // Only left wants to be open, check if it meets requirement
      const leftWidth = getPanelWidth('left')
      const centerWidth = window.innerWidth - leftWidth - ICONBAR_WIDTH
      if (centerWidth < MIN_CENTER_WIDTH) {
        closePanel('left')
      }
    } else if (rightShouldBeOpen) {
      // Only right wants to be open, check if it meets requirement
      const rightWidth = getPanelWidth('right')
      const centerWidth = window.innerWidth - rightWidth - ICONBAR_WIDTH
      if (centerWidth < MIN_CENTER_WIDTH) {
        closePanel('right')
      }
    }
  }

  /**
   * Handle window resize
   */
  const handleWindowResize = () => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT

    if (isMobile) {
      // Mobile: ensure only one panel is open
      if (isPanelOpen('left') && isPanelOpen('right')) {
        closePanel('left')
      }
      return
    }

    // Desktop: Check center width
    const leftOpen = isPanelOpen('left')
    const rightOpen = isPanelOpen('right')

    if (leftOpen && rightOpen) {
      const centerWidth = getCenterWidth()
      if (centerWidth < MIN_CENTER_WIDTH) {
        // Close left, prioritize right
        closePanel('left')

        // Check if right alone meets requirement
        setTimeout(() => {
          const newCenterWidth = getCenterWidth()
          if (newCenterWidth < MIN_CENTER_WIDTH) {
            closePanel('right')
          }
        }, 50)
      }
    } else if (leftOpen) {
      const centerWidth = getCenterWidth()
      if (centerWidth < MIN_CENTER_WIDTH) {
        closePanel('left')
      }
    } else if (rightOpen) {
      const centerWidth = getCenterWidth()
      if (centerWidth < MIN_CENTER_WIDTH) {
        closePanel('right')
      }
    }
  }

  /**
   * Handle panel open events - check if opening would violate rule
   */
  const handlePanelOpen = e => {
    // Don't interfere if this event came from the coordinator
    if (e.detail && e.detail.fromCoordinator) return

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
    const openingPanel = e.type === 'open-left-drawer' ? 'left' : 'right'
    const oppositePanel = openingPanel === 'left' ? 'right' : 'left'

    if (isMobile) {
      // Mobile: close opposite panel
      if (isPanelOpen(oppositePanel)) {
        closePanel(oppositePanel)
      }
      return
    }

    // Desktop: Check if both being open would violate rule
    if (isPanelOpen(oppositePanel)) {
      // Calculate what center width would be with both open
      setTimeout(() => {
        const centerWidth = getCenterWidth()
        if (centerWidth < MIN_CENTER_WIDTH) {
          closePanel(oppositePanel)
        }
      }, 50)
    }
  }

  /**
   * Track when user starts resizing a panel
   */
  const handleResizeStart = resizingPanel => {
    if (!resizingPanel) return

    const oppositePanel = resizingPanel === 'left' ? 'right' : 'left'

    resizeState.current = {
      isResizing: true,
      resizingPanel,
      oppositeWasOpen: isPanelOpen(oppositePanel),
    }
  }

  /**
   * Handle active resizing - dynamically open/close opposite panel
   */
  const handleActiveResize = () => {
    if (!resizeState.current.isResizing) return

    const { resizingPanel, oppositeWasOpen } = resizeState.current
    const oppositePanel = resizingPanel === 'left' ? 'right' : 'left'
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT

    if (isMobile) return // No dynamic behavior on mobile

    const centerWidth = getCenterWidth()
    const oppositeIsOpen = isPanelOpen(oppositePanel)

    // If center is too small and opposite is open, close it
    if (centerWidth < MIN_CENTER_WIDTH && oppositeIsOpen) {
      closePanel(oppositePanel)
    }
    // If center is big enough and opposite was originally open but is now closed, reopen it
    else if (
      centerWidth >= MIN_CENTER_WIDTH &&
      !oppositeIsOpen &&
      oppositeWasOpen
    ) {
      openPanel(oppositePanel)
    }
  }

  /**
   * Track when resizing ends
   */
  const handleResizeEnd = () => {
    if (resizeState.current.isResizing) {
      resizeState.current = {
        isResizing: false,
        resizingPanel: null,
        oppositeWasOpen: false,
      }
    }
  }

  /**
   * Initialize coordination on mount
   */
  useEffect(() => {
    // Small delay to ensure all panels are rendered
    const initTimeout = setTimeout(() => {
      coordinateOnLoad()
    }, 150)

    // Listen for window resize
    let resizeTimeout
    const debouncedResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(handleWindowResize, 100)
    }
    window.addEventListener('resize', debouncedResize)

    // Listen for panel open events
    window.addEventListener('open-left-drawer', handlePanelOpen)
    window.addEventListener('open-chat-drawer', handlePanelOpen)
    // Note: Right panel doesn't dispatch open-chat-drawer, it just adds the class
    // So we need to observe class changes on body
    const bodyObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const hadChatDrawer =
            mutation.oldValue && mutation.oldValue.includes('has-chat-drawer')
          const hasChatDrawer =
            document.body.classList.contains('has-chat-drawer')

          if (!hadChatDrawer && hasChatDrawer) {
            handlePanelOpen({ type: 'open-chat-drawer', detail: {} })
          }
        }
      })
    })
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    })

    // Listen for resize start
    const handleMouseDown = e => {
      const leftHandle = e.target.closest('.left-resize-handle')
      const rightHandle = e.target.closest('.resize-handle')

      if (leftHandle) {
        handleResizeStart('left')
      } else if (rightHandle) {
        handleResizeStart('right')
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('touchstart', handleMouseDown)

    // Listen for keyboard resize start
    const handleKeyboardResize = e => {
      if (e.detail && e.detail.panel) {
        handleResizeStart(e.detail.panel)
      }
    }
    window.addEventListener('keyboard-resize', handleKeyboardResize)

    // Listen during resize
    let resizeAnimationFrame
    const onResize = () => {
      if (
        document.body.classList.contains('resizing') ||
        resizeState.current.isResizing
      ) {
        handleActiveResize()
        resizeAnimationFrame = requestAnimationFrame(onResize)
      }
    }
    const resizeObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const isResizing =
            document.body.classList.contains('resizing-left') ||
            document.body.classList.contains('resizing-right')
          if (isResizing) {
            resizeAnimationFrame = requestAnimationFrame(onResize)
          } else {
            cancelAnimationFrame(resizeAnimationFrame)
            handleResizeEnd()
          }
        }
      })
    })
    resizeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })

    // Cleanup
    return () => {
      clearTimeout(initTimeout)
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', debouncedResize)
      window.removeEventListener('open-left-drawer', handlePanelOpen)
      window.removeEventListener('open-chat-drawer', handlePanelOpen)
      bodyObserver.disconnect()
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('touchstart', handleMouseDown)
      window.removeEventListener('keyboard-resize', handleKeyboardResize)
      resizeObserver.disconnect()
      cancelAnimationFrame(resizeAnimationFrame)
    }
  }, [coursenum])

  // This component doesn't render anything
  return null
}

PanelCoordinator.propTypes = {
  coursenum: PropTypes.string.isRequired,
}
